import { useEffect, useRef, useState, useCallback } from 'react';
import { isNil } from 'lodash';
import { useLocation, useNavigationType } from 'react-router-dom';

import { useTrackEvent } from '@/lib/analytics';
import { useTitle } from '@/lib/hooks/use-title';
import { useKonstaabel } from '@/lib/hooks/use-konstaabel';
import { useLayoutContext } from '@/components/layout/layout-context';
import { useKeyPress } from '@/lib/hooks/use-key-press';
import {
  getExplorationLayout,
  isEmptyExploration,
  setExplorationLayout,
  getCleanedExplorationName,
  setExplorationCanvas,
  setExplorationTabs,
  deleteExplorationTab,
  addExporationTab,
  moveExplorationTab,
  getExplorationTabs,
  moveCellsToTab,
} from '@/core/exploration';
import { HideInEmbedded } from '@/components/layout/hide-in-embedded';
import { useBuildAccountUrl } from '@/lib/accounts/context';
import { generateShortId } from '@/lib/utils/string';

import { useDirtyContext } from '../dirty-context';
import { Exploration, Cell, ExplorationLayout, ChatMode } from '../types';
import { BlockParameter, buildExplorationUrl, getHashParameter } from '../utils';
import { duplicateExplorationTab } from './utils';
import { measureCanvasNodes } from '../components/canvas/utils';
import { Sidebar } from './sidebar';
import { useExplorationContext } from './exploration-context';
import { useExplorationUIContext } from './exploration-ui-context';
import { ExplorationHeader } from './header';
import { ExplorationCanvas } from './canvas';
import { ExplorationNotebook } from './notebook';

import styles from './exploration.module.scss';

export const ExplorationView = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cellsRef = useRef<HTMLDivElement | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [showDataCatalog, setShowDataCatalog] = useState(false);
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const location = useLocation();
  const navigationType = useNavigationType();

  const { confirmUnsavedChangesIfNeeded } = useDirtyContext();

  useKonstaabel();

  const {
    exploration,
    parameters,
    setExploration,
    addCells,
    copiedCell,
    removeCopiedCell,
    createConversation,
  } = useExplorationContext();

  const {
    selectedCell,
    isEditorOpen,
    scrollToId,
    highlightedCellId,
    closeEditor,
    openEditor,
    scrollToCell,
    selectCell,
    deselectCell,
    newCellIndex,
    setNewCellIndex,
    newConversationSubjectId,
    setNewConversationSubjectId,
    activeTabId,
    setActiveTabId,
  } = useExplorationUIContext();

  const trackEvent = useTrackEvent();
  const { isRightSidebarOpen, isEmbeddedMode } = useLayoutContext();
  const buildAccountUrl = useBuildAccountUrl();

  useKeyPress('Escape', () => isEditorOpen && closeEditor(), { includeInputs: true });

  useTitle(getCleanedExplorationName(exploration.name));

  useEffect(() => {
    if (navigationType === 'POP') {
      setNewCellIndex(null); // reset new cell index when navigating back
    }
  }, [navigationType, setNewCellIndex]);

  useEffect(() => {
    setNewCellIndex(null);
    trackEvent('Exploration Opened', {
      explorationId: exploration.explorationId,
      explorationSourceId: exploration.options?.explorationSourceId,
    });
  }, [exploration.options?.explorationSourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // "cell" parameter is used for backward compatibility since older URLs to blocks used "cell" instead of "block".
    const scrollToCellId =
      getHashParameter(location.hash, BlockParameter) ?? getHashParameter(location.hash, 'cell');

    if (scrollToCellId !== null) {
      selectCell(scrollToCellId);
      scrollToCell(scrollToCellId);
      return;
    }
  }, [location.hash, selectCell, scrollToCell, exploration, activeTabId, setActiveTabId]);

  const handleAddTab = (label: string) => {
    const tab = {
      tabId: generateShortId(),
      label,
      cellIds: [],
    };
    setExploration(addExporationTab(exploration, tab));
    trackEvent('Tab Added', {
      explorationId: exploration.explorationId,
      tabId: tab.tabId,
      label,
    });
  };

  const handleRenameTab = (tabId: string, label: string) => {
    setExploration(
      setExplorationTabs(
        exploration,
        getExplorationTabs(exploration).map((tab) => {
          if (tabId !== tab.tabId) {
            return tab;
          }

          return {
            ...tab,
            label,
          };
        }),
      ),
    );
    trackEvent('Exploration Tab Renamed', {
      explorationId: exploration.explorationId,
      tabId,
      label,
    });
  };

  const handleDuplicateTab = (tabId: string) => {
    const result = duplicateExplorationTab(exploration, tabId);
    setExploration(result.exploration);

    if (result.tab) {
      setRenamingTabId(result.tab.tabId);
    }

    trackEvent('Exploration Tab Duplicated', {
      explorationId: exploration.explorationId,
      sourceTabId: tabId,
      newTabId: result.tab?.tabId,
      cellCount: result.tab?.cellIds.length ?? 0,
    });
  };

  const handleDeleteTab = (tabId: string) => {
    const updatedExploration = deleteExplorationTab(exploration, tabId);
    setExploration(updatedExploration);
    trackEvent('Exploration Tab Deleted', {
      explorationId: exploration.explorationId,
      tabId,
    });
  };

  const handleMoveTab = (fromIndex: number, toIndex: number, after: boolean) => {
    const updatedExploration = moveExplorationTab(exploration, fromIndex, toIndex, after);
    setExploration(updatedExploration);
    trackEvent('Exploration Tab Moved', {
      explorationId: exploration.explorationId,
      fromIndex,
      toIndex: after ? toIndex + 1 : toIndex,
    });
  };

  const handleMoveCellToTab = (cellId: string, tabId: string) => {
    setExploration(moveCellsToTab(exploration, [cellId], tabId));
    trackEvent('Exploration Cell Moved to Tab', {
      explorationId: exploration.explorationId,
      tabId,
      cellId,
    });
  };

  const handleAddCell = useCallback(
    (index: number, tabId?: string | null) => {
      confirmUnsavedChangesIfNeeded({
        onConfirm: () => {
          closeEditor();
          if (!isNil(tabId) && tabId !== activeTabId) {
            setActiveTabId(tabId, true);
          }
          setNewCellIndex(index);
        },
      });
    },
    [confirmUnsavedChangesIfNeeded, closeEditor, activeTabId, setNewCellIndex, setActiveTabId],
  );

  const emptyExploration = isEmptyExploration(exploration);

  const handleLayoutChange = (value: ExplorationLayout) => {
    const canvasState = exploration.view.canvas ?? {
      edges: [],
      nodes: cellsRef.current !== null ? measureCanvasNodes(cellsRef.current) : [],
    };
    setExploration(setExplorationCanvas(setExplorationLayout(exploration, value), canvasState));

    if (value === 'notebook' && selectedCell !== null) {
      scrollToCell(selectedCell.id);
    }
  };

  const handleClickExploration = (selectedExploration: Exploration, index: number) => {
    const [cell, ...dependencies] = selectedExploration.view.cells;
    const result = addCells(cell, dependencies, index);
    const cellId = result.cell.id;

    selectCell(cellId);
    scrollToCell(cellId);

    // Make sure to open editors for funnel and cohort because their empty states are not useful.
    if (cell.kind === 'funnel' || cell.kind === 'cohort') {
      openEditor(cellId);
    }

    setNewCellIndex(null);
    trackEvent('Exploration Cells Added', {
      explorationId: exploration.explorationId,
      selectedExploration: selectedExploration.explorationId,
      selectedName: selectedExploration.name,
      atIndex: index,
    });
  };

  const handleCellPaste = (
    copiedCell: { cell: Cell; dependencies: Cell[] } | undefined,
    cellIndex: number,
  ) => {
    if (isNil(copiedCell)) {
      return;
    }

    const { cell, dependencies } = addCells(copiedCell.cell, copiedCell.dependencies, cellIndex);

    selectCell(cell.id);
    scrollToCell(cell.id);
    setNewCellIndex(null);
    setShowDataCatalog(false);
    removeCopiedCell();

    trackEvent('Exploration Cell Pasted', {
      explorationId: exploration.explorationId,
      atIndex: cellIndex,
      cellId: cell.id,
      numDependencies: dependencies.length,
    });
  };

  const handleCreateConversation = async (
    searchQuery: string,
    sources: string[],
    mode: ChatMode,
    index: number,
  ) => {
    const correlationId = crypto.randomUUID();
    const originatingExplorationUrl = buildAccountUrl(buildExplorationUrl(exploration));
    const {
      conversation,
      error,
      exploration: updatedExploration,
    } = await createConversation(searchQuery, sources, mode, index, correlationId);
    const resultExplorationUrl = buildAccountUrl(buildExplorationUrl(updatedExploration));
    if (error !== undefined) {
      trackEvent('Conversation Create Failed', {
        message: searchQuery,
        conversationId: conversation?.conversationId,
        correlationId,
        originatingExplorationUrl,
        resultExplorationUrl,
        isResearch: mode === 'research',
        sources,
        hasCurrentExploration: exploration.view.cells.length > 0,
        error: error.message,
      });
    } else {
      trackEvent('Conversation Create Success', {
        message: searchQuery,
        conversationId: conversation?.conversationId,
        correlationId,
        originatingExplorationUrl,
        resultExplorationUrl,
        isResearch: mode === 'research',
        sources,
        hasCurrentExploration: exploration.view.cells.length > 0,
      });
    }

    return { conversation, error };
  };

  return (
    <div className={styles.explorationContent} ref={containerRef}>
      <div className={styles.splitView}>
        <div className={styles.main}>
          <HideInEmbedded>
            {emptyExploration ? null : (
              <ExplorationHeader
                exploration={exploration}
                onLayoutChange={handleLayoutChange}
                onMoveTab={handleMoveTab}
                onMoveCellToTab={handleMoveCellToTab}
              />
            )}
          </HideInEmbedded>
          {getExplorationLayout(exploration) === 'canvas' ? (
            <ExplorationCanvas
              exploration={exploration}
              setExploration={setExploration}
              selectCell={selectCell}
              deselectCell={deselectCell}
              showDataCatalog={showDataCatalog}
              setShowDataCatalog={setShowDataCatalog}
              newCellIndex={newCellIndex}
              setNewCellIndex={setNewCellIndex}
              newConversationSubjectId={newConversationSubjectId}
              setNewConversationSubjectId={setNewConversationSubjectId}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onAsk={handleCreateConversation}
              onExplorationClick={handleClickExploration}
              copiedCell={copiedCell}
              onBlockAdd={(index) => handleAddCell(index, activeTabId)}
              onCellPaste={handleCellPaste}
            />
          ) : (
            <ExplorationNotebook
              exploration={exploration}
              setExploration={setExploration}
              selectCell={selectCell}
              deselectCell={deselectCell}
              showDataCatalog={showDataCatalog}
              setShowDataCatalog={setShowDataCatalog}
              newCellIndex={newCellIndex}
              setNewCellIndex={setNewCellIndex}
              newConversationSubjectId={newConversationSubjectId}
              setNewConversationSubjectId={setNewConversationSubjectId}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onAsk={handleCreateConversation}
              onExplorationClick={handleClickExploration}
              copiedCell={copiedCell}
              onBlockAdd={(index) => handleAddCell(index, activeTabId)}
              onCellPaste={handleCellPaste}
              parameters={parameters}
              scrollToCell={scrollToCell}
              isEditorOpen={isEditorOpen}
              closeEditor={closeEditor}
              openEditor={openEditor}
              selectedCell={selectedCell}
              scrollToId={scrollToId}
              highlightedCellId={highlightedCellId}
              embeddedMode={isEmbeddedMode}
              cellsRef={cellsRef}
              containerRef={containerRef}
            />
          )}
        </div>
        <HideInEmbedded>
          {!emptyExploration && (
            <Sidebar
              activeTabId={activeTabId}
              collapsed={!isRightSidebarOpen}
              onAddCell={(index, tabId) => handleAddCell(index, tabId)}
              onAddTab={handleAddTab}
              onRenameTab={handleRenameTab}
              onDuplicateTab={handleDuplicateTab}
              onDeleteTab={handleDeleteTab}
              onMoveTab={handleMoveTab}
              renamingTabId={renamingTabId}
              onSetRenamingTab={setRenamingTabId}
              onClearRenamingTab={() => setRenamingTabId(null)}
            />
          )}
        </HideInEmbedded>
      </div>
    </div>
  );
};
